import React, { useMemo, useState } from 'react';
import { Button, Card, Flex, Table, Tag, Typography } from '@duxton-web/ui';
import type { TableColumnsType } from '@duxton-web/ui';

import {
  DatadogMonitorStatus,
  getAggregateMonitorStatus,
  getDatadogMonitors,
  getDatadogMonitorStatusColor,
  getLatestMonitorStatuses,
  getMonitorValue,
  MonitorCheckEntry,
  MonitorMetricConfig,
  normalizeDatadogMonitorStatus,
} from './utils';

interface CanaryMonitorStatusPanelProps {
  entries?: MonitorCheckEntry[];
  metricsConfig?: MonitorMetricConfig[];
  phaseStatus?: string;
  compact?: boolean;
}

const DEFAULT_VISIBLE_CHECKS = 5;

interface MonitorStatusRow extends MonitorCheckEntry {
  key: string;
  statuses: Array<{
    key: string;
    status: DatadogMonitorStatus;
  }>;
}

const StatusTag = ({ status }: { status: DatadogMonitorStatus }) => (
  <Tag color={getDatadogMonitorStatusColor(status)}>{status}</Tag>
);

export const CanaryMonitorStatusPanel: React.FC<
  CanaryMonitorStatusPanelProps
> = ({ entries = [], metricsConfig, phaseStatus, compact = false }) => {
  const [showAllChecks, setShowAllChecks] = useState(false);
  const monitors = getDatadogMonitors(metricsConfig);
  const latestStatuses = getLatestMonitorStatuses(
    entries,
    metricsConfig,
    phaseStatus,
  );
  const aggregateStatus = getAggregateMonitorStatus(
    latestStatuses.map(({ status }) => status),
  );

  const rows = useMemo(
    () =>
      [...entries].reverse().map((entry, index) => ({
        ...entry,
        key: `${entry.checkedAt}-${index}`,
        statuses: monitors.map((monitor) => ({
          key: monitor.key,
          status: normalizeDatadogMonitorStatus(
            getMonitorValue(entry.metrics, monitor),
            { unavailable: !!entry.error },
          ),
        })),
      })),
    [entries, monitors],
  );

  const columns = useMemo<TableColumnsType<MonitorStatusRow>>(
    () => [
      {
        title: 'Checked at',
        dataIndex: 'checkedAt',
        key: 'checkedAt',
        render: (checkedAt: string) => new Date(checkedAt).toLocaleTimeString(),
      },
      ...monitors.map((monitor, monitorIndex) => ({
        title: monitor.name,
        key: monitor.key,
        render: (_: unknown, row: MonitorStatusRow) => (
          <StatusTag
            status={row.statuses[monitorIndex]?.status ?? 'Data unavailable'}
          />
        ),
      })),
    ],
    [monitors],
  );

  if (monitors.length === 0) return null;

  const visibleRows = showAllChecks
    ? rows
    : rows.slice(0, DEFAULT_VISIBLE_CHECKS);
  const statusSurface = STATUS_SURFACE[aggregateStatus];

  return (
    <Card
      size="small"
      style={{
        marginTop: 8,
        borderColor: statusSurface.border,
        background: statusSurface.background,
      }}
      styles={{
        body: {
          padding: compact ? 8 : '10px 12px',
          background: statusSurface.background,
        },
      }}
    >
      <Flex vertical gap={compact ? 6 : 8}>
        <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
          <Flex vertical>
            <Typography.Text strong>Datadog monitor status</Typography.Text>
            <Typography.Text
              type="secondary"
              style={{ display: 'block', fontSize: 11 }}
            >
              Checked every minute during this phase. Only Alert stops rollout.
            </Typography.Text>
          </Flex>
          <StatusTag status={aggregateStatus} />
        </Flex>

        <Flex wrap="wrap" gap={8}>
          {latestStatuses.map(({ key, name, status }) => (
            <Flex
              key={key}
              align="center"
              gap={8}
              style={{
                padding: '4px 8px',
                border: '1px solid #e8e8e8',
                borderRadius: 6,
                background: '#fff',
              }}
            >
              <Typography.Text style={{ fontSize: 12 }}>{name}</Typography.Text>
              <StatusTag status={status} />
            </Flex>
          ))}
        </Flex>

        {phaseStatus === 'yet_to_start' ? (
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            Checks begin when this phase starts.
          </Typography.Text>
        ) : rows.length === 0 ? (
          <Typography.Text type="secondary" style={{ fontSize: 11 }}>
            No monitor checks recorded for this phase.
          </Typography.Text>
        ) : (
          <>
            <Table<MonitorStatusRow>
              columns={columns}
              dataSource={visibleRows}
              pagination={false}
              rowKey="key"
              scroll={{ x: 'max-content' }}
              size="small"
            />
            {rows.length > DEFAULT_VISIBLE_CHECKS && (
              <Button
                type="link"
                size="small"
                onClick={() => setShowAllChecks((current) => !current)}
                style={{ padding: 0, alignSelf: 'flex-start' }}
              >
                {showAllChecks
                  ? 'Show latest 5 checks'
                  : `Show all ${rows.length} checks`}
              </Button>
            )}
          </>
        )}
      </Flex>
    </Card>
  );
};

const STATUS_SURFACE: Record<
  DatadogMonitorStatus,
  { border: string; background: string }
> = {
  OK: { border: '#b7eb8f', background: '#f6ffed' },
  Warn: { border: '#ffe58f', background: '#fffbe6' },
  Alert: { border: '#ffccc7', background: '#fff2f0' },
  'Data unavailable': { border: '#d9d9d9', background: '#fafafa' },
  'Not checked': { border: '#d9d9d9', background: '#fafafa' },
};

export {
  getAggregateMonitorStatus,
  getDatadogMonitorNames,
  getDatadogMonitorStatusColor,
  getLatestMonitorStatuses,
  isDatadogMonitorConfig,
  isDatadogMonitorName,
  normalizeDatadogMonitorStatus,
  withoutDatadogMonitors,
} from './utils';
